import React, { useEffect, forwardRef, useImperativeHandle } from 'react'
import { Box, Grid, Typography } from '@mui/material'
import DisplayItem from '../../Components/DisplayItems/DisplayItem'
import DisplayItemForm from '../../Components/Form/DisplayItemForm'
import DefDossierDefFieldForm from '../../Components/Form/DefDossierDefFieldForm'
import DefFieldForm from '../../Components/Form/DefFieldForm'
import DefDossierLinkForm from '../../Components/Form/DefDossierLinkForm'
import AdminQuickEditMenu from '../../Components/Elements/AdminQuickEditMenu'
import { useDefaultGrid } from './useDefaultGrid'
import {
    getTranslationFromItem,
    replaceSpecialTags,
    isObligatory,
    isEditMode,
    getMinimalColspan,
    checkCondition,
    shouldListenForConditions,
    isView,
    returnContent,
    renderInformationText,
    conditionMatches,
    conditionRuleFieldMatches,
} from './utils'

interface DefaultGridProps {
    items: any[]
    mode: string
    kedo: any
    content?: any
    errors?: any
    dossier?: any
    embedded?: boolean
    linkId?: number
    conditions?: any[]
    hiddenfields?: any[]
    changeValue?: (itemId: string, value: any) => void
    onKeyPress?: (event: React.KeyboardEvent) => void
    fetch?: () => void
    customButtons?: () => React.ReactNode
    custom_layout?: string
    multiUpdate?: boolean
    withoutEdit?: boolean
}

export interface DefaultGridHandle {
    doFocus: (errorId: string) => void
    onCancel: () => void
    getValues: () => { [key: string]: any }
    conditionMatches: (condition: any, values: any) => boolean
    conditionRuleFieldMatches: (fieldRule: any, values: any) => boolean
    isObligatory: (item: any, conditions: any, callback: () => any) => boolean
    checkCondition: (
        conditions: any,
        hiddenfields: any,
        item: any,
        condAct?: string
    ) => boolean
    recheckConditions: () => void
    isEditMode: (mode?: string) => boolean
    isView: (currentView: string, itemView: string) => boolean
}

const DefaultGrid = forwardRef<DefaultGridHandle, DefaultGridProps>(
    (props, ref) => {
        const {
            docheck,
            firstcheck,
            withoutEdit,
            editDdl,
            editDdi,
            editDddf,
            editDf,
            ddiRefs,
            doFocus,
            onCancel,
            getValues,
            recheckConditions,
            setFirstcheck,
            setEditDdl,
            setEditDdi,
            setEditDddf,
            setEditDf,
        } = useDefaultGrid(props)

        useImperativeHandle(ref, () => ({
            doFocus,
            onCancel,
            getValues,
            conditionMatches: (condition: any, values: any) =>
                conditionMatches(condition, values),
            conditionRuleFieldMatches: (fieldRule: any, values: any) =>
                conditionRuleFieldMatches(fieldRule, values),
            isObligatory: (item: any, conditions: any, callback: () => any) =>
                isObligatory(item, conditions, callback || getValues),
            checkCondition: (
                conditions: any,
                hiddenfields: any,
                item: any,
                condAct = 'show_fields'
            ) =>
                checkCondition(
                    conditions,
                    hiddenfields,
                    item,
                    condAct,
                    getValues
                ),
            recheckConditions,
            isEditMode: (mode?: string) => isEditMode(mode || props.mode),
            isView: (currentView: string, itemView: string) =>
                isView(currentView, itemView),
        }))

        useEffect(() => {
            if (!firstcheck) {
                setTimeout(() => setFirstcheck(true), 150)
            }
        }, [firstcheck, setFirstcheck])

        const renderPreText = (item: any) => {
            const trans = getTranslationFromItem(item, props.kedo)
            if (props.mode === 'search' || props.mode === 'advanced-search') {
                return null
            }
            if (!trans || !trans.settings || !trans.settings.pretext) {
                return null
            }
            return (
                <Typography
                    component="span"
                    sx={{
                        color: (theme) => theme.palette.text.secondary, // $secondary
                    }}
                >
                    {replaceSpecialTags(trans.settings.pretext)}
                </Typography>
            )
        }

        const renderLabel = (item: any) => {
            if (props.custom_layout === 'row_input') {
                return null
            }
            if (!item.translations || item.translations.length <= 0) {
                return item.id
            }
            if (
                props.embedded &&
                item.def_dossier_link &&
                item.def_dossier_link.id === props.linkId
            ) {
                return null
            }
            const trans = getTranslationFromItem(item, props.kedo)
            if (
                !props.dossier &&
                item.def_dossier_link &&
                item.def_dossier_link.type_of_link === 'embeddedList' &&
                props.mode !== 'search'
            ) {
                if (trans.length <= 0) {
                    return ''
                }
            }
            return (
                <>
                    <Typography
                        component="h6"
                        data-ddiid={item.id}
                        sx={{
                            color: (theme) => theme.palette.text.primary, // $body-color equivalent
                            fontSize: '1rem',
                            fontWeight: 700,
                            lineHeight: 1.2,
                            mt: 0,
                            mb: '0.5rem',
                            '& .fa-question-circle': {
                                fontSize: '14px',
                            },
                        }}
                    >
                        {trans.name ? trans.name : ''}&nbsp;
                        {isEditMode(props.mode) &&
                            isObligatory(item, props.conditions, getValues) && (
                                <span style={{ color: '#F38B8F' }}>*</span>
                            )}
                        &nbsp;
                        {!item.settings?.show_info_direct &&
                            renderInformationText(
                                trans,
                                item.settings?.show_info_direct
                            )}
                        <AdminQuickEditMenu kedo={props.kedo} item={item} />
                    </Typography>
                    {item.settings?.show_info_direct &&
                        renderInformationText(
                            trans,
                            item.settings.show_info_direct
                        )}
                    <AdminQuickEditMenu kedo={props.kedo} item={item} />
                </>
            )
        }

        const displayGridSx = {
            p: 1.25,
            '& h6': {
                color: 'text.primary',
                '& .fa-question-circle': {
                    fontSize: '14px',
                },
            },
            '& .PreText': {
                color: 'text.secondary',
            },
        }

        const breakSx = {
            width: '100% !important',
            display: {
                xs: 'none !important',
                md: 'block !important',
            },
        }

        const renderDisplayItems = () => {
            let currentRank: number | null = null
            let breakClassLine = false
            return props.items
                .filter((item) => isView(props.mode, item.view))
                .sort((a, b) => a.rank - b.rank)
                .map((item, index) => {
                    if (currentRank === null) {
                        currentRank = item.rank
                    }
                    if (item.rank !== currentRank) {
                        breakClassLine = true
                        currentRank = item.rank
                    } else {
                        breakClassLine = false
                    }
                    if (
                        firstcheck &&
                        !checkCondition(
                            props.conditions,
                            props.hiddenfields,
                            item,
                            'show_fields',
                            getValues
                        )
                    ) {
                        return null
                    }
                    if (
                        isEditMode(props.mode) &&
                        item.def_dossier_def_field &&
                        item.def_dossier_def_field.def_field &&
                        item.def_dossier_def_field.def_field.type ===
                            'autoIncrement'
                    ) {
                        return null
                    }
                    const checkConditionsOnChange = shouldListenForConditions(
                        props.conditions,
                        item
                    )

                    const itemSx = {
                        ...displayGridSx,
                        width: '100% !important', // xs full width
                        flexBasis: {
                            xs: '100% !important',
                            md: `${
                                (getMinimalColspan(item) / 12) * 100
                            }% !important`,
                        },
                        maxWidth: {
                            xs: '100% !important',
                            md: `${
                                (getMinimalColspan(item) / 12) * 100
                            }% !important`,
                        },
                        ...(props.custom_layout === 'row_input'
                            ? {
                                  width: '100% !important',
                                  flexBasis: '100% !important',
                                  maxWidth: '100% !important',
                              }
                            : {}),
                        ...(props.custom_layout === 'row_input' &&
                            (item?.def_dossier_def_field?.def_field.type ===
                                'list' ||
                                (item?.def_dossier_link?.id > 0 &&
                                    item.settings?.enable_small_view)) && {
                                minWidth: '200px !important',
                            }),
                    }

                    return (
                        <React.Fragment key={item.id}>
                            {breakClassLine && (
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: getMinimalColspan(item),
                                    }}
                                    sx={breakSx}
                                />
                            )}

                            <Box sx={itemSx}>
                                {renderPreText(item)}
                                {renderLabel(item)}
                                <DisplayItem
                                    changeValue={props.changeValue}
                                    conditions={props.conditions}
                                    content={returnContent(
                                        item,
                                        props.content,
                                        props.mode
                                    )}
                                    dossier={props.dossier}
                                    errors={
                                        item &&
                                        item.id &&
                                        props.errors &&
                                        props.errors[item.id]
                                            ? props.errors[item.id]
                                            : null
                                    }
                                    item={item}
                                    kedo={props.kedo}
                                    embedded={props.embedded}
                                    linkId={props.linkId}
                                    key={item.id}
                                    mode={props.mode}
                                    custom_layout={props.custom_layout}
                                    onKeyPress={
                                        props.onKeyPress
                                            ? props.onKeyPress
                                            : undefined
                                    }
                                    recheckConditions={recheckConditions}
                                    ref={ddiRefs.current[`ddiRef${item.id}`]}
                                    shoudListenForConditions={
                                        checkConditionsOnChange
                                    }
                                />
                            </Box>
                        </React.Fragment>
                    )
                })
        }

        return (
            <Grid
                container
                sx={{
                    ...(props.custom_layout === 'row_input' && {
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'baseline',
                    }),
                    mx: -1.25, // equivalent to margin: 0 -10px
                }}
            >
                {editDdl ? (
                    <DefDossierLinkForm
                        defDossier={editDdl.def_dossier_id}
                        onClose={() => setEditDdl(null)}
                        item={editDdl.def_dossier_link}
                        kedo={props.kedo}
                        onSuccess={() => {
                            setEditDdl(null)
                            props.fetch && props.fetch()
                        }}
                    />
                ) : null}
                {editDdi ? (
                    <DisplayItemForm
                        defDossier={editDdi.def_dossier_id}
                        onClose={() => {
                            setEditDdi(null)
                            props.fetch && props.fetch()
                        }}
                        id={editDdi.id}
                        kedo={props.kedo}
                    />
                ) : null}
                {editDddf ? (
                    <DefDossierDefFieldForm
                        defDossier={editDddf.def_dossier_id}
                        onClose={() => {
                            setEditDddf(null)
                            props.fetch && props.fetch()
                        }}
                        item={editDddf.def_dossier_def_field}
                        kedo={props.kedo}
                    />
                ) : null}
                {editDf ? (
                    <DefFieldForm
                        defDossier={editDf.def_dossier_id}
                        onClose={() => {
                            setEditDf(null)
                            props.fetch && props.fetch()
                        }}
                        item={editDf.def_dossier_def_field.def_field}
                        kedo={props.kedo}
                    />
                ) : null}
                {renderDisplayItems()}
                {props.customButtons ? props.customButtons() : null}
            </Grid>
        )
    }
)

DefaultGrid.displayName = 'DefaultGrid'

export default DefaultGrid
